import { useTranslation } from "react-i18next";
import { BaseAssignmentResponseDtoQuestionsItem } from "@packages/api/madlenAPI.schemas";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createQuestionRegen,
  getGetAssignmentFromTeacherQueryKey,
  useApplyQuestionRegenToQuestion,
  useGetQuestionRegenWithRegenId,
} from "@packages/api/madlenAPI";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Check,
  Wand,
  Pencil,
  X,
} from "lucide-react";
import Modal from "@core/modal";
import { getEnvironment } from "@packages/utils";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { TYPE_CONVERSION_PRESETS, PRESETS } from "./constants";
import { VersionGrid } from "./VersionGrid";

const RegenerateComponent = ({
  regenPrompt,
  setRegenPrompt,
}: {
  regenPrompt: string;
  setRegenPrompt: (value: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  return (
    <div className="space-y-4">
      <Text variant="h4" className="font-medium">
        {t("assignments.generator.preview.regeneratePrompt")}
      </Text>
      <div className="flex flex-wrap gap-2 mb-3">
        <Button
          onClick={() =>
            setRegenPrompt(
              t("assignments.generator.preview.prompts.makeHarder"),
            )
          }
          size="md"
          rounded="md"
          bgColor="bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
          textColor="text-blue-700 dark:text-blue-100"
        >
          {t("assignments.generator.preview.makeHarder")}
        </Button>
        <Button
          onClick={() =>
            setRegenPrompt(
              t("assignments.generator.preview.prompts.makeEasier"),
            )
          }
          size="md"
          rounded="md"
          bgColor="bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800"
          textColor="text-green-700 dark:text-green-100"
        >
          {t("assignments.generator.preview.makeEasier")}
        </Button>
        <Button
          onClick={() =>
            setRegenPrompt(
              t("assignments.generator.preview.prompts.rewriteSame"),
            )
          }
          size="md"
          rounded="md"
          bgColor="bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800"
          textColor="text-purple-700 dark:text-purple-100"
        >
          {t("assignments.generator.preview.rewriteSame")}
        </Button>
        <Button
          onClick={() =>
            setRegenPrompt(
              t("assignments.generator.preview.prompts.makeClearer"),
            )
          }
          size="md"
          rounded="md"
          bgColor="bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900 dark:hover:bg-yellow-800"
          textColor="text-yellow-700 dark:text-yellow-100"
        >
          {t("assignments.generator.preview.makeClearer")}
        </Button>
      </div>
      <textarea
        value={regenPrompt}
        onChange={(e) => setRegenPrompt(e.target.value)}
        placeholder={t(
          "assignments.generator.preview.regeneratePromptPlaceholder",
        )}
        className="w-full min-h-[100px] p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
};

const RegenOverlay = ({
  question,
  show,
  onCloseHandler,
  resetToOriginalQuestion,
}: {
  question: BaseAssignmentResponseDtoQuestionsItem;
  show: boolean;
  onCloseHandler: () => void;
  resetToOriginalQuestion: (questionId: number) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [isRegenLoading, setIsRegenLoading] = useState<boolean>(false);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [lastRegenId, setLastRegenId] = useState<number | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [showRegenComponent, setShowRegenComponent] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [mode, setMode] = useState<"preset" | "custom">("preset");
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number | null>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState<string>(
    t("assignments.generator.preview.prompts.makeHarder"),
  );
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [isInitialRender, setIsInitialRender] = useState<boolean>(true);

  useEffect(() => {
    if (show) {
      setIsInitialRender(true);
      const timer = setTimeout(() => {
        setIsInitialRender(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [show]);

  useEffect(() => {
    if (show && question?.id) {
      const shouldShowVersions = localStorage.getItem(
        `show-versions-${question.id}`,
      );
      if (shouldShowVersions) {
        setShowRegenComponent(false);
        localStorage.removeItem(`show-versions-${question.id}`);
      }
    }
  }, [show, question?.id]);

  useEffect(() => {
    if (show) {
      setHasAnimated(false);
      const timer = setTimeout(() => {
        setHasAnimated(true);
      }, 1000); // Set this after all initial animations complete
      return () => clearTimeout(timer);
    }
  }, [show]);

  const typeConversionPresets = useMemo(() => TYPE_CONVERSION_PRESETS, []);

  const presets = useMemo(() => PRESETS, []);

  const filteredTypeConversionPresets = useMemo(() => {
    if (!question?.type) return [];
    return typeConversionPresets.filter(
      (preset) => preset.targetType !== question.type,
    );
  }, [question?.type, typeConversionPresets]);

  const handlePresetClick = useCallback(
    (preset: (typeof presets)[0] | (typeof typeConversionPresets)[0]) => {
      setSelectedPreset(preset.id);
      setMode("preset");
      setCustomPrompt("");
      setRegenPrompt(preset.prompt);
    },
    [],
  );

  const handleCustomPromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const textarea = e.target;
      cursorPositionRef.current = textarea.selectionStart;

      setCustomPrompt(textarea.value);
      if (mode !== "custom") {
        setMode("custom");
        setSelectedPreset(null);
      }
      setRegenPrompt(textarea.value);

      // Restore cursor position after state update
      requestAnimationFrame(() => {
        if (textareaRef.current && cursorPositionRef.current !== null) {
          textareaRef.current.selectionStart = cursorPositionRef.current;
          textareaRef.current.selectionEnd = cursorPositionRef.current;
        }
      });
    },
    [mode],
  );

  const RegenerateComponent = useCallback(() => {
    return (
      <div className="space-y-4 relative">
        {/* Title */}
        <div className="text-center">
          <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text text-transparent dark:from-indigo-400 dark:to-blue-300 title-gradient">
            {t("assignments.generator.preview.regeneratePrompt")}
          </h3>
        </div>

        {/* Question Type Conversion Section */}
        {/* {filteredTypeConversionPresets.length > 0 && (
          <div className="mb-8">
            <div className="relative">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400">
                  {t(
                    "assignments.generator.preview.questionTypeConversion.title",
                  )}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-1">
              {filteredTypeConversionPresets.map((preset, index) => (
                <Button
                  key={preset.id}
                  onClick={() => handlePresetClick(preset)}
                  rounded="lg"
                  size="md"
                  bgColor={
                    selectedPreset === preset.id
                      ? `${preset.bgColor} ring-1 ${preset.ringColor} shadow-md ${preset.shadowColor}`
                      : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }
                  borderColor={
                    selectedPreset === preset.id
                      ? `border-2 ${preset.color}-200 dark:border-${preset.color}-800`
                      : "border-2 border-gray-100 dark:border-gray-700"
                  }
                  className={`
                    group relative
                    flex flex-col items-center gap-2 hover:-translate-y-0.5
                    ${!hasAnimated ? "preset-button" : ""}
                  `}
                  style={
                    !hasAnimated
                      ? { animationDelay: `${index * 100}ms` }
                      : undefined
                  }
                >
                  <span className="text-2xl transform group-hover:scale-110 transition-transform duration-300">
                    {preset.icon}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-center">
                    {t(
                      `assignments.generator.preview.presets.${preset.id}.title`,
                    )}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    {t(
                      `assignments.generator.preview.presets.${preset.id}.description`,
                    )}
                  </span>
                </Button>
              ))}
            </div>
          </div>
        )} */}

        {/* Divider */}
        {/* <div className="relative">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-3 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400">
              {t("assignments.generator.preview.modifyQuestion")}
            </span>
          </div>
        </div> */}

        {/* Original Presets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mx-auto">
          {presets.map((preset, index) => (
            <Button
              key={preset.id}
              onClick={() => handlePresetClick(preset)}
              rounded="lg"
              size="md"
              bgColor={
                selectedPreset === preset.id
                  ? `${preset.bgColor} ring-1 ${preset.ringColor} shadow-md ${preset.shadowColor}`
                  : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              }
              borderColor={
                selectedPreset === preset.id
                  ? `border-2 ${preset.color}-200 dark:border-${preset.color}-800`
                  : "border-2 border-gray-100 dark:border-gray-700"
              }
              className={`
                group relative flex items-center gap-3 hover:-translate-y-0.5
                ${!hasAnimated ? "preset-button" : ""}
              `}
              style={
                !hasAnimated
                  ? { animationDelay: `${index * 100}ms` }
                  : undefined
              }
            >
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center shadow-inner
                  ${
                    selectedPreset === preset.id
                      ? `bg-${preset.color}-100 dark:bg-${preset.color}-900/50 shadow-${preset.color}-500/20`
                      : "bg-gray-100 dark:bg-gray-700"
                  }
                  group-hover:scale-110 transition-transform duration-300
                  ${!hasAnimated ? "preset-icon" : ""}
                `}
                style={
                  !hasAnimated
                    ? { animationDelay: `${index * 100 + 200}ms` }
                    : undefined
                }
              >
                <span className="text-xl transform group-hover:rotate-12 transition-transform duration-300">
                  {preset.icon}
                </span>
              </div>

              <div
                className={`flex-1 text-left ${!hasAnimated ? "preset-text" : ""}`}
                style={
                  !hasAnimated
                    ? { animationDelay: `${index * 100 + 300}ms` }
                    : undefined
                }
              >
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {t(
                    `assignments.generator.preview.presets.${preset.id}.title`,
                  )}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t(
                    `assignments.generator.preview.presets.${preset.id}.description`,
                  )}
                </p>
              </div>

              {selectedPreset === preset.id && (
                <div
                  className={`absolute -right-1 -top-1 p-1.5 rounded-full bg-${preset.color}-500 text-white shadow-lg animate-bounce`}
                >
                  <Check className="w-3 h-3" />
                </div>
              )}
            </Button>
          ))}

          {/* Custom Input Option */}
          <div
            className={`
            relative w-full md:w-[95%] m-1 p-3 rounded-lg transition-all duration-300 col-span-1 md:col-span-3
            ${
              mode === "custom"
                ? "bg-purple-50 dark:bg-purple-900/30 ring-1 ring-purple-500 shadow-md shadow-purple-500/20"
                : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            }
            shadow-sm hover:shadow-md hover:-translate-y-0.5 transform transition-all duration-300
          `}
          >
            <div className="flex items-start gap-3">
              <div
                className={`
    w-10 aspect-square rounded-full flex items-center justify-center
    ${
      mode === "custom"
        ? "bg-purple-100 dark:bg-purple-900/50 shadow-purple-500/20"
        : "bg-gray-100 dark:bg-gray-700"
    }
    transition-all duration-300
  `}
              >
                <Pencil className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>

              <textarea
                ref={textareaRef}
                value={customPrompt}
                onChange={handleCustomPromptChange}
                placeholder={
                  t("assignments.generator.preview.presets.custom.title") +
                  ": " +
                  t("assignments.generator.preview.presets.custom.placeholder")
                }
                className="w-full p-3 h-20 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 shadow-inner"
                autoFocus={mode === "custom"}
                rows={1}
              />

              {mode === "custom" && customPrompt && (
                <div className="absolute -right-1 -top-1 p-1.5 rounded-full bg-purple-500 text-white shadow-lg shadow-purple-500/30 animate-bounce">
                  <Check className="w-3 h-3" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    mode,
    selectedPreset,
    customPrompt,
    presets,
    handlePresetClick,
    handleCustomPromptChange,
    t,
    hasAnimated,
    filteredTypeConversionPresets,
  ]);

  const oldGenerations = useMemo(
    () =>
      Object.entries(JSON.parse(JSON.stringify(localStorage)))
        .filter(([key]) => key.startsWith(`regen-${question?.id}`))
        .sort((a, b) => {
          const aParts = a[0].split("-");
          const bParts = b[0].split("-");
          if (aParts.length !== bParts.length) {
            return aParts.length - bParts.length;
          }
          return Number(aParts[2] || 0) - Number(bParts[2] || 0);
        }),
    [localStorage, question?.id, lastRegenId],
  );

  const maxPage = Math.ceil(oldGenerations.length / 2) - 1;

  const handleVersionSelect = (index: number) => {
    console.log("Version selected:", {
      index,
      currentPage,
      absoluteIndex: currentPage * 2 + index,
      generation: oldGenerations[currentPage * 2 + index],
    });

    // Add sparkle effect
    const element = document.querySelector(
      `[data-version-index="${currentPage * 2 + index}"]`,
    );
    if (element) {
      const sparkleContainer = document.createElement("div");
      sparkleContainer.className =
        "absolute inset-0 overflow-hidden pointer-events-none";

      for (let i = 0; i < 10; i++) {
        const sparkle = document.createElement("div");
        sparkle.className =
          "absolute w-2 h-2 bg-blue-500 rounded-full animate-sparkle";
        sparkle.style.left = `${Math.random() * 100}%`;
        sparkle.style.top = `${Math.random() * 100}%`;
        sparkle.style.animationDelay = `${Math.random() * 500}ms`;
        sparkleContainer.appendChild(sparkle);
      }

      element.appendChild(sparkleContainer);
      setTimeout(() => sparkleContainer.remove(), 1000);
    }

    setSelectedVersion(currentPage * 2 + index);
  };

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(maxPage, prev + 1));
  }, [maxPage]);

  const visibleGenerations = oldGenerations.slice(
    currentPage * 2,
    currentPage * 2 + 2,
  );

  const queryClient = useQueryClient();
  const getAssignmentFromTeacherQueryKey = getGetAssignmentFromTeacherQueryKey(
    Number(question?.assignmentId),
  );

  const { queryKey: regenFetchQueryKey } = useGetQuestionRegenWithRegenId(
    lastRegenId!,
    {
      query: {
        enabled: lastRegenId !== null,
        refetchInterval: (response) => {
          if (response?.state.data?.output) {
            setIsRegenLoading(false);
            if (question.id && lastRegenId && response.state.data.output) {
              const questionToBeSave = {
                ...response.state.data,
                regenPrompt: regenPrompt,
              };
              localStorage.setItem(
                `regen-${question.id}-${lastRegenId}`,
                JSON.stringify(questionToBeSave),
              );
              setCurrentPage(Math.floor(oldGenerations.length / 2));
              setShowRegenComponent(false);
              setLastRegenId(null);
            }
            return false;
          }
          return 1000;
        },
      },
    },
  );

  const { mutateAsync: applyQuestionRegenToQuestion } =
    useApplyQuestionRegenToQuestion({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getAssignmentFromTeacherQueryKey,
          });
        },
        onError: () => {
          setLastRegenId(null);
          queryClient.resetQueries({
            queryKey: regenFetchQueryKey,
          });
          setRegenPrompt(t("assignments.generator.preview.prompts.makeHarder"));
        },
        onSettled: () => {
          onCloseHandler();
        },
      },
    });

  const regenerateQuestion = async () => {
    setIsButtonLoading(true);

    try {
      if (selectedVersion === null) {
        setIsRegenLoading(true);
      }

      if (localStorage.getItem(`regen-${question.id}`) === null) {
        localStorage.setItem(`regen-${question.id}`, JSON.stringify(question));
      }

      if (selectedVersion !== null) {
        // Store the selected key before closing modal
        const selectedKey = oldGenerations[selectedVersion][0];

        // Add magical closing animation
        const modalContent = document.querySelector(".version-grid");
        if (modalContent) {
          modalContent.classList.add("magical-close-animation");

          // Add sparkle burst effect
          const sparkleBurst = document.createElement("div");
          sparkleBurst.className = "sparkle-burst";
          document.body.appendChild(sparkleBurst);

          // Remove sparkle burst after animation
          setTimeout(() => sparkleBurst.remove(), 1000);
        }

        // Close modal immediately
        onCloseHandler();

        if (selectedVersion === 0) {
          resetToOriginalQuestion(question.id);
          return;
        }

        // Get the target question element
        const questionElement = document.querySelector(".question-content");
        if (questionElement) {
          // Add fade out animation
          questionElement.classList.add("fade-out");
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // Apply the change
        await applyQuestionRegenToQuestion({
          data: {
            questionId: question.id,
            questionRegenId: Number(selectedKey.split("-")[2]),
          },
        });

        return;
      }

      const regenRequest = await createQuestionRegen({
        id: question.id,
        prompt: regenPrompt,
        environment: getEnvironment(),
      });

      if (regenRequest.id) {
        setLastRegenId(regenRequest.id);
      }
    } catch (error) {
      setIsRegenLoading(false);
    } finally {
      setIsButtonLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={onCloseHandler}
      title=""
      hideCloseButton={true}
      actions={[
        {
          label: t("common.pick"),
          onClick: () => {
            if (showRegenComponent && !selectedPreset && !customPrompt.trim()) {
              return;
            }
            if (
              !showRegenComponent &&
              selectedVersion === null &&
              oldGenerations.length > 0
            ) {
              setShowWarning(true);
              return;
            }
            if (
              !isRegenLoading &&
              !(showRegenComponent && !selectedPreset && !customPrompt.trim())
            ) {
              regenerateQuestion();
            }
          },
          variant: "primary",
          loading: isButtonLoading,
        },
      ]}
      outerClassName={`w-[80vw] max-w-[900px] transform-gpu -m-12 ${isInitialRender ? "animate-modal-enter" : ""}`}
    >
      <div
        className={`w-full ${isInitialRender ? "animate-content-enter" : ""} max-h-[calc(90vh-120px)] overflow-y-auto p-1`}
      >
        {!showRegenComponent ? (
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between border-b dark:border-gray-700 pb-4">
              <Text variant="h3" className="font-bold text-xl animate-fade-in">
                {t("assignments.generator.preview.pickQuestion")}
              </Text>
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => {
                    setShowRegenComponent(true);
                    setIsButtonLoading(false);
                    setSelectedVersion(null);
                  }}
                  size="sm"
                  rounded="md"
                  bgColor="bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
                  textColor="text-blue-700 dark:text-blue-100"
                  className="flex items-center gap-2 animate-fade-in"
                >
                  <Plus size={14} />
                  {t("assignments.generator.preview.generateNew")}
                </Button>
                <Button
                  onClick={onCloseHandler}
                  size="sm"
                  rounded="full"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                  className="p-1.5"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </Button>
              </div>
            </div>

            {showWarning && selectedVersion === null && (
              <Text
                variant="p"
                className="text-amber-500 dark:text-amber-400 text-sm font-medium animate-fade-in -mt-4"
              >
                {t("assignments.generator.preview.warnings.selectVersion")}
              </Text>
            )}

            <div className="relative px-4">
              {oldGenerations.length > 0 && !isRegenLoading && (
                <div className="w-full">
                  <VersionGrid
                    currentPage={currentPage}
                    visibleGenerations={visibleGenerations}
                    selectedVersion={selectedVersion}
                    onVersionSelect={handleVersionSelect}
                    t={t}
                  />
                  {currentPage > 0 && (
                    <Button
                      onClick={goToPreviousPage}
                      size="sm"
                      rounded="full"
                      bgColor="bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-700"
                      className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 p-2 shadow-lg"
                    >
                      <ChevronLeft className="w-5 h-5 text-blue-500" />
                    </Button>
                  )}
                  {currentPage < maxPage && (
                    <Button
                      onClick={goToNextPage}
                      size="sm"
                      rounded="full"
                      bgColor="bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-700"
                      className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 p-2 shadow-lg"
                    >
                      <ChevronRight className="w-5 h-5 text-blue-500" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <Button
              onClick={onCloseHandler}
              size="sm"
              rounded="full"
              bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
              className="p-1.5 absolute top-4 right-4"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </Button>

            <RegenerateComponent />
          </div>
        )}
      </div>

      {isRegenLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm transition-all duration-300">
          <div
            className={`relative animate-fade-in z-10 ${!isRegenLoading ? "loading-out" : ""}`}
          >
            <div className="absolute -inset-6 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-xl opacity-75 blur-lg animate-pulse"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-300/30 via-transparent to-transparent animate-ping"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 rounded-xl p-8 flex flex-col items-center gap-4 shadow-xl border border-blue-200 dark:border-blue-900 animate-float transform-gpu">
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full blur animate-spin-slow"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-full p-4">
                  <Wand className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-bounce" />
                </div>
              </div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:from-blue-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent animate-pulse">
                {t("assignments.generator.preview.regenerating")}
              </h3>
              <div className="flex gap-2 mt-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-bounce"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default RegenOverlay;
